import { readFileAsArrayBuffer } from './fileFingerprint';
import { readWorkbookFromArrayBuffer, readSheetMatrix } from './xlsxUtils';
import type { CukcukWorkerSheetData } from '../workers/cukcukXlsx.worker';

export interface CukcukWorkbookData {
  sheetNames: string[];
  sheets: Record<string, CukcukWorkerSheetData>;
}

const readWorkbookOnMainThread = async (file: File): Promise<CukcukWorkbookData> => {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = readWorkbookFromArrayBuffer(buffer);
  const sheetNames = workbook.SheetNames ?? [];
  const sheets: Record<string, CukcukWorkerSheetData> = {};

  sheetNames.forEach((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    if (worksheet) {
      const hiddenColumns: number[] = [];
      (worksheet['!cols'] ?? []).forEach((col, index) => {
        if (col?.hidden) hiddenColumns.push(index);
      });

      sheets[sheetName] = {
        matrix: readSheetMatrix(worksheet),
        hiddenColumns,
      };
    }
  });

  return { sheetNames, sheets };
};

/** Read workbook matrices; uses Web Worker in browser when available. */
export const readCukcukWorkbookData = async (file: File): Promise<CukcukWorkbookData> => {
  if (typeof window !== 'undefined' && typeof Worker !== 'undefined') {
    try {
      const runtime = await import('./cukcukXlsxWorkerRuntime');
      return runtime.readCukcukWorkbookDataInWorker(file);
    } catch {
      // Fall back to main-thread parsing.
    }
  }

  return readWorkbookOnMainThread(file);
};

export const getSheetMatrix = (sheet: CukcukWorkerSheetData | undefined): unknown[][] =>
  sheet?.matrix ?? [];

export const getSheetHiddenColumns = (sheet: CukcukWorkerSheetData | undefined): Set<number> =>
  new Set(sheet?.hiddenColumns ?? []);